import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

const sizeClasses = {
  xs: 'max-w-xs',
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-xl',
  xl: 'max-w-3xl',
  full: 'max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)]',
};

const Modal = ({
  open = false,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeOnEscape = true,
  closeOnOverlay = true,
  showClose = true,
  className = '',
  bodyClassName = '',
  persistent = false,
}) => {
  const contentRef = useRef(null);
  const previousFocus = useRef(null);
  const onCloseRef = useRef(onClose);
  const prevOpenRef = useRef(false);

  // Keep onClose ref current without triggering effect re-runs
  onCloseRef.current = onClose;

  // Stable keydown handler (no onClose in deps — uses ref)
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && closeOnEscape) {
        if (persistent) {
          contentRef.current?.classList.add('animate-wiggle');
          setTimeout(() => contentRef.current?.classList.remove('animate-wiggle'), 500);
        } else {
          onCloseRef.current?.();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, closeOnEscape, persistent]);

  // Auto-focus + body lock — only on open transition (false → true)
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      previousFocus.current = document.activeElement;
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        contentRef.current?.querySelector(
          'input, select, textarea, button, [href], [tabindex]:not([tabindex="-1"])',
        )?.focus();
      });
    }
    if (!open && prevOpenRef.current) {
      document.body.style.overflow = '';
      previousFocus.current?.focus();
    }
    prevOpenRef.current = open;
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      if (persistent) {
        contentRef.current?.classList.add('animate-wiggle');
        setTimeout(() => contentRef.current?.classList.remove('animate-wiggle'), 500);
      } else if (closeOnOverlay) {
        onCloseRef.current?.();
      }
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      {/* Frosted backdrop */}
      <div
        className="absolute inset-0 bg-surface-950/30 backdrop-blur-[8px] animate-modal-overlay"
        onClick={handleOverlayClick}
        aria-hidden="true"
      />

      {/* Content — premium glass card */}
      <div
        ref={contentRef}
        className={`
          relative w-full ${sizeClasses[size]}
          bg-white backdrop-blur-xl shadow-modal
          border border-surface-200/40
          animate-modal-content
          flex flex-col max-h-[calc(100vh-2rem)]
          ${className}
        `}
        style={{ borderRadius: 'var(--radius-modal)' }}
      >
        {/* Header */}
        {(title || showClose) && (
          <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-0">
            <div className="flex-1 min-w-0">
              {title && (
                <h2 className="text-[15px] font-bold text-surface-900 font-display">
                  {title}
                </h2>
              )}
              {description && (
                <p className="text-[12px] text-surface-600 mt-0.5">{description}</p>
              )}
            </div>
            {showClose && (
              <button
                onClick={onClose}
                className="flex-shrink-0 p-1.5 -mr-1 text-surface-500 hover:text-surface-700 hover:bg-surface-100 rounded-[10px] transition-all duration-200"
                aria-label="Close"
              >
                <X size={15} />
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className={`flex-1 overflow-y-auto px-6 py-5 ${bodyClassName}`}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-2.5 px-6 py-4 border-t border-surface-100/80 bg-surface-50/30" style={{ borderRadius: '0 0 var(--radius-modal) var(--radius-modal)' }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

Modal.Header = ({ children, className = '' }) => (
  <div className={`mb-1 ${className}`}>{children}</div>
);
Modal.Body = ({ children, className = '' }) => (
  <div className={className}>{children}</div>
);
Modal.Footer = ({ children, className = '' }) => (
  <div className={`flex items-center justify-end gap-2 ${className}`}>{children}</div>
);

export default Modal;
